<!-- eslint-disable vue/no-mutating-props -->
<template>
    <div class="mb-10">
        <p class="text-subtitle-1 font-weight-medium">{{ $t('capacity_and_beds') }}:</p>

        <!-- Capacity Section -->
        <v-row dense class="mb-6">
            <v-col cols="12" md="5">
                <v-row dense>
                    <v-col cols="12" sm="5" md="5">
                        <p class="text-subtitle-1">
                            {{ $t('base_occupancy') }}*
                            <v-tooltip location="top" activator="parent" class="text-body-2" max-width="360">
                                {{ $t('product.info.capacity_note') }}
                            </v-tooltip>
                        </p>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            ref="minCapacityInput"
                            v-model="modelValue.minOccupancy"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="min-capacity"
                            hide-details
                            width="70px"
                            :rules="[
                                requiredRule('product.info.capacity'),
                                onlyInteger('product.info.capacity'),
                                moreThanZero('product.info.capacity'),
                                notMoreThanMaxOccupancy('product.info.capacity'),
                            ]"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                            @update:model-value="emit('update-min-total-occupancy')"
                        />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.maxOccupancy"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="max-capacity"
                            hide-details
                            width="70px"
                            :rules="[
                                requiredRule('product.info.capacity'),
                                onlyInteger('product.info.capacity'),
                                moreThanZero('product.info.capacity'),
                            ]"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="5" md="5">
                        <p class="text-subtitle-1">
                            {{ $t('extra_persons_with_extra_bed') }}*
                            <v-tooltip location="top" activator="parent" class="text-body-2" max-width="360">
                                {{ $t('product.info.extra_bed_note') }}
                            </v-tooltip>
                        </p>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <div style="width: 70px" />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.maxExtraPlace"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="extra-bed"
                            hide-details
                            width="70px"
                            :rules="[
                                requiredRule('product.info.extra_bed'),
                                onlyInteger('product.info.extra_bed'),
                                moreThanZero('product.info.extra_bed'),
                            ]"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="5" md="5">
                        <p class="text-subtitle-1">
                            {{ $t('extra_persons_without_extra_bed') }}*
                            <v-tooltip location="top" activator="parent" class="text-body-2" max-width="360">
                                {{ $t('product.info.sharing_place_note') }}
                            </v-tooltip>
                        </p>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <div style="width: 70px" />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.maxWithoutPlace"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="max-sharing-place"
                            hide-details
                            width="70px"
                            :rules="[
                                requiredRule('product.info.sharing_place'),
                                onlyInteger('product.info.sharing_place'),
                                moreThanZero('product.info.sharing_place'),
                            ]"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="5" md="5">
                        <p class="text-subtitle-1">
                            {{ $t('total_occupancy') }}*
                            <v-tooltip location="top" activator="parent" class="text-body-2" max-width="360">
                                {{ $t('product.info.total_note') }}
                            </v-tooltip>
                        </p>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.minTotalOccupancy"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="min-total"
                            hide-details
                            width="70px"
                            disabled
                        />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.maxTotalOccupancy"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="max-total"
                            hide-details
                            width="70px"
                            disabled
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="5">
                <v-row dense>
                    <v-col cols="12" sm="5" md="4">
                        <p class="text-subtitle-1">{{ $t('number_of_bedrooms') }}</p>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.bedroomsCount"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="bedrooms-quantity"
                            hide-details
                            width="70px"
                            :rules="modelValue.bedroomsCount ? [onlyInteger('number_of_bedrooms')] : []"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="5" md="4">
                        <p class="text-subtitle-1">{{ $t('number_of_bathrooms') }}</p>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-text-field
                            v-model="modelValue.bathroomsCount"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="bathrooms-quantity"
                            hide-details
                            width="70px"
                            :rules="modelValue.bathroomsCount ? [onlyInteger('number_of_bathrooms')] : []"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Bed Types Section -->
        <v-row v-if="hasBedTypes" dense class="mb-6">
            <v-col cols="12" md="5">
                <v-row dense class="align-center flex-nowrap">
                    <v-col cols="8" md="6" class="flex-shrink-1">
                        <p class="text-subtitle-1 font-weight-medium">{{ $t('bed_type') }}:</p>
                    </v-col>
                    <v-col cols="3" class="text-right">
                        <p class="text-subtitle-1 font-weight-medium">{{ $t('number_of_beds') }}:</p>
                    </v-col>
                    <v-btn
                        v-if="!isHotelDescriptionMode && !isSpecialRoomType"
                        size="small"
                        icon="mdi-plus"
                        variant="text"
                        color="primary"
                        data-cy="add-row"
                        width="40px"
                        @click="emit('add-bed-row', false)"
                    />
                </v-row>
                <v-row
                    v-for="bedType in modelValue.beds?.filter(({alternative}) => !alternative)"
                    :key="bedType.betTypeId"
                    dense
                    class="flex-nowrap"
                >
                    <v-col cols="9" md="7" class="flex-shrink-1">
                        <v-autocomplete
                            v-model="bedType.betTypeId"
                            :items="bedTypes"
                            item-value="id"
                            item-title="name"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            :data-cy="`bed-type-${bedType.betTypeId}}`"
                            hide-details
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                            :rules="[requiredRule('bed_type')]"
                        />
                    </v-col>
                    <v-col cols="2" style="min-width: 100px">
                        <v-autocomplete
                            v-model="bedType.count"
                            :items="useNumbers"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            :data-cy="`quantity-${bedType.id}}`"
                            hide-details
                            :rules="[requiredRule('number_of_beds')]"
                            :readonly="isFieldReadonly"
                            :disabled="isFieldDisabled"
                        />
                    </v-col>
                    <v-btn
                        v-if="!isHotelDescriptionMode && !isSpecialRoomType"
                        size="small"
                        icon="mdi-close"
                        variant="text"
                        color="error"
                        :data-cy="`remove-${bedType.id}`"
                        min-width="40px"
                        @click="emit('remove-bed-row', bedType.id)"
                    />
                </v-row>
            </v-col>
            <v-col v-if="hasAlternateBedTypes || (!isHotelDescriptionMode && !isSpecialRoomType)" cols="12" md="5">
                <template v-if="useIsShowAlternative">
                    <v-row dense class="align-center flex-nowrap">
                        <v-col cols="8" md="6" class="flex-shrink-1">
                            <p class="text-subtitle-1 font-weight-medium">
                                {{ $t('alternate_bed_type') }}:
                                <span>
                                    <v-icon size="small" class="text-info"> mdi-information-outline </v-icon>
                                    <v-tooltip location="top" activator="parent" class="text-body-2" max-width="360">
                                        {{ $t('alternate_bed_note') }}
                                    </v-tooltip>
                                </span>
                            </p>
                        </v-col>
                        <v-col cols="3" class="text-right">
                            <p class="text-subtitle-1 font-weight-medium">{{ $t('number_of_beds') }}:</p>
                        </v-col>
                        <v-btn
                            v-if="!isHotelDescriptionMode && !isSpecialRoomType"
                            size="small"
                            icon="mdi-plus"
                            variant="text"
                            color="primary"
                            data-cy="add-row"
                            width="40px"
                            @click="emit('add-bed-row', true)"
                        />
                    </v-row>
                    <v-row
                        v-for="bedType in modelValue.beds?.filter(({alternative}) => alternative)"
                        :key="bedType.betTypeId"
                        dense
                        class="flex-nowrap"
                    >
                        <v-col cols="9" md="7" class="flex-shrink-1">
                            <v-autocomplete
                                v-model="bedType.betTypeId"
                                :items="bedTypes"
                                item-value="id"
                                item-title="name"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                :data-cy="`bed-type-${bedType.betTypeId}}`"
                                hide-details
                                :readonly="isFieldReadonly"
                                :disabled="isFieldDisabled"
                                :rules="[requiredRule('bed_type')]"
                            />
                        </v-col>
                        <v-col cols="2" style="min-width: 100px">
                            <v-autocomplete
                                v-model="bedType.count"
                                :items="useNumbers"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                :data-cy="`quantity-${bedType.betTypeId}}`"
                                hide-details
                                :rules="[requiredRule('number_of_beds')]"
                                :readonly="isFieldReadonly"
                                :disabled="isFieldDisabled"
                            />
                        </v-col>
                        <v-btn
                            v-if="!isHotelDescriptionMode && !isSpecialRoomType"
                            size="small"
                            icon="mdi-close"
                            variant="text"
                            color="error"
                            :data-cy="`remove-${bedType.id}`"
                            min-width="40px"
                            @click="emit('remove-bed-row', bedType.id)"
                        />
                    </v-row>
                </template>
                <template v-if="!isHotelDescriptionMode && !isSpecialRoomType">
                    <v-btn
                        v-if="!useIsShowAlternative"
                        variant="text"
                        color="primary"
                        class="text-caption px-1 mt-2"
                        density="compact"
                        @click="emit('add-bed-row', true)"
                    >
                        {{ $t('add_alternate_bed_types') }}
                    </v-btn>
                    <v-btn
                        v-if="useIsShowAlternative"
                        variant="text"
                        color="primary"
                        class="text-caption px-1 mt-4"
                        density="compact"
                        @click="emit('remove-alternate-bed-types')"
                    >
                        {{ $t('remove_alternate_bed_types') }}
                    </v-btn>
                </template>
            </v-col>
        </v-row>

        <!-- Guest Allocation Options -->
        <template v-if="hasGuestAllocationOptions || (!isHotelDescriptionMode && !isSpecialRoomType)">
            <p class="text-subtitle-1 font-weight-medium">{{ $t('options_of_guests') }}</p>
            <p class="text-subtitle-1 font-italic">{{ $t('options_of_guests_note') }}</p>
            <div class="mb-6 overflow-x-auto">
                <v-row dense class="align-center flex-nowrap" no-gutters style="gap: 5px">
                    <p class="text-subtitle-1 font-weight-medium flex-shrink-0" style="width: 100px">
                        {{ $t('adults') }}
                    </p>
                    <p class="text-subtitle-1 font-weight-medium flex-shrink-0" style="width: 100px">
                        {{ $t('children') }}
                    </p>
                    <p class="text-subtitle-1 font-weight-medium flex-shrink-0" style="width: 205px">
                        {{ $t('child_age') }}
                    </p>
                    <v-btn
                        v-if="!isHotelDescriptionMode && !isSpecialRoomType"
                        class="flex-shrink-0"
                        size="small"
                        icon="mdi-plus"
                        variant="text"
                        color="primary"
                        data-cy="add-row"
                        @click="emit('add-row')"
                    />
                </v-row>
                <p v-if="isShowValidationError" class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular">
                    {{ $t('guests_allocation_error') }}
                </p>
                <v-row
                    v-for="guest in modelValue.allocationOptions"
                    :key="guest.id"
                    class="mb-3 flex-nowrap"
                    no-gutters
                    align="start"
                    style="gap: 5px"
                >
                    <v-autocomplete
                        v-model="guest.adults"
                        :items="useNumbersWithZero"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :data-cy="`guest-adults-${guest.id}}`"
                        hide-details
                        :readonly="isFieldReadonly"
                        :disabled="isFieldDisabled"
                        class="flex-shrink-0 flex-grow-0"
                        width="100"
                    />
                    <v-autocomplete
                        v-model="guest.children"
                        :items="useChildrenNumbers"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :data-cy="`guest-children-${guest.id}}`"
                        hide-details
                        :readonly="isFieldReadonly"
                        :disabled="isFieldDisabled"
                        width="100"
                        class="flex-shrink-0 flex-grow-0"
                        @update:model-value="emit('update-children-ages-row', guest.id)"
                    />
                    <div style="width: 205px">
                        <template v-if="guest.children">
                            <v-row
                                v-for="(children, index) in guest.hotelRoomTypeAllocationOptionAges"
                                :key="index"
                                class="flex-nowrap pb-2"
                                no-gutters
                                style="gap: 5px"
                            >
                                <v-autocomplete
                                    v-model="children.minAge"
                                    :items="useChildrenAges"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :data-cy="`age-from-${index}}`"
                                    hide-details
                                    :readonly="isFieldReadonly"
                                    :disabled="isFieldDisabled"
                                    class="flex-shrink-0"
                                    width="100"
                                />
                                <v-autocomplete
                                    v-model="children.maxAge"
                                    :items="useChildrenAges"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :data-cy="`age-from-${index}}`"
                                    hide-details
                                    :readonly="isFieldReadonly"
                                    :disabled="isFieldDisabled"
                                    class="flex-shrink-0"
                                    width="100"
                                />
                            </v-row>
                        </template>
                    </div>
                    <v-btn
                        v-if="!isHotelDescriptionMode && !isSpecialRoomType"
                        size="small"
                        icon="mdi-close"
                        variant="text"
                        color="error"
                        :data-cy="`remove-${guest.id}`"
                        class="flex-shrink-0"
                        min-width="40px"
                        @click="emit('remove-row', guest.id)"
                    />
                </v-row>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import {onlyInteger, moreThanZero, requiredRule} from '~/composables/rules'

    const props = defineProps<{
        modelValue: any
        bedTypes: any[]
        isFieldReadonly: boolean
        isFieldDisabled: boolean
        isHotelDescriptionMode: boolean
        isSpecialRoomType: boolean
        isShowValidationError: boolean
    }>()

    const emit = defineEmits<{
        'update:modelValue': [value: any]
        'update-min-total-occupancy': []
        'add-bed-row': [alternative: boolean]
        'remove-bed-row': [id: string]
        'remove-alternate-bed-types': []
        'add-row': []
        'remove-row': [id: string]
        'update-children-ages-row': [id: string]
    }>()

    const {t} = useI18n()

    const useNumbers = computed(() => {
        return Array.from({length: 20}, (_, index) => index + 1)
    })

    const useNumbersWithZero = computed(() => {
        return Array.from(Array(21).keys())
    })

    const useChildrenNumbers = computed(() => {
        return Array.from(Array(11).keys())
    })

    const useChildrenAges = computed(() => {
        return Array.from(Array(18).keys())
    })

    const hasBedTypes = computed(() => {
        return props.modelValue?.beds?.filter(({alternative}) => !alternative)?.length > 0
    })

    const hasAlternateBedTypes = computed(() => {
        return props.modelValue?.beds?.filter(({alternative}) => alternative)?.length > 0
    })

    const hasGuestAllocationOptions = computed(() => {
        return props.modelValue?.allocationOptions?.length > 0
    })

    const useIsShowAlternative = computed(() => {
        if (!props.modelValue?.beds?.length) return false
        return props.modelValue?.beds.filter(({alternative}) => alternative)?.length > 0
    })

    const notMoreThanMaxOccupancy = (prop: string) => {
        return v => v <= props.modelValue.maxOccupancy || `${t('validation.valid', {prop})}`
    }

    // Total occupancy is derived, not editable: base occupancy + extra persons on extra bed
    // + extra persons without a place (they still occupy the room and are counted by the search).
    // Values arrive as strings from the number inputs — coerce before summing (2 + "1" must be 3, not "21").
    watch(
        () => [props.modelValue.maxOccupancy, props.modelValue.maxExtraPlace, props.modelValue.maxWithoutPlace],
        ([maxOccupancy, maxExtraPlace, maxWithoutPlace]) => {
            props.modelValue.maxTotalOccupancy =
                (Number(maxOccupancy) || 0) + (Number(maxExtraPlace) || 0) + (Number(maxWithoutPlace) || 0)
        },
        {immediate: true}
    )

    // ZSH-882: notMoreThanMaxOccupancy depends on maxOccupancy, but Vuetify only
    // re-runs a field's rules on its own change/blur. Re-validate min when max
    // changes so its error state (and the form validity gating Save) stays in sync
    // — otherwise min stays red / Save stays disabled until a manual focus->blur.
    const minCapacityInput = ref<any>(null)
    watch(
        () => props.modelValue.maxOccupancy,
        () => {
            if (props.modelValue.minOccupancy != null) {
                minCapacityInput.value?.validate?.()
            }
        }
    )
</script>
